import { Alert, Empty, Spin } from 'antd';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../../components/button';
import { PageTopBar } from '../../components/page-top-bar';
import { TopTabs } from '../../components/top-tabs';
import { useDoc31Store } from '../../contexts/mobx';
import { useCheckNavigation } from '../../contexts/unsaved-changes';
import { DOC31_PAGE_TITLE } from '../../utils/doc31';
import { useSearchParams } from '../../utils/use-search-params';
import { Doc31CouponsTab } from './coupons-tab';
import { EditionHeader } from './edition-header';
import { Doc31HistoryTab } from './history-tab';
import { useDoc31ReasonPrompt } from './reason-prompt';
import {
    Doc31CatalogTab,
    Doc31SetupFormProvider,
    Doc31SetupTab,
} from './setup-tab';
import style from './style.module.scss';

type Doc31Tab = 'setup' | 'catalog' | 'coupons' | 'history';

type Doc31SearchParams = {
    edition?: string;
    tab?: Doc31Tab;
    coupon?: number;
};

/**
 * 31 Days of Circle K (DOC31-33): one edition at a time, picked in the
 * header; its setup, bonuses and badges, coupons and history in tabs. Setup
 * and Bonuses & badges share one provider: a form and a Save per section,
 * one unsaved-changes guard. The edition lives in the URL. A link may also
 * name a tab or a coupon; they are read once and then kept on screen only,
 * so moving between tabs never trips the unsaved-changes guard while the
 * forms stay mounted.
 */
export const Doc31Page = observer(() => {
    const store = useDoc31Store();
    const reasonPrompt = useDoc31ReasonPrompt();
    const { checkNavigation } = useCheckNavigation();
    const { params, setParams } = useSearchParams<Doc31SearchParams>();
    const [editionsLoad, setEditionsLoad] = useState<
        'loading' | 'loaded' | 'failed'
    >('loading');
    const [tab, setTab] = useState<Doc31Tab>(() => params.tab ?? 'setup');
    const [focusCouponId, setFocusCouponId] = useState<number | null>(() =>
        typeof params.coupon === 'number' ? params.coupon : null
    );
    const requestedKey =
        params.edition === undefined ? undefined : String(params.edition);

    const loadEditions = useCallback(() => {
        setEditionsLoad('loading');
        store
            .fetchEditions()
            .then((ok) => setEditionsLoad(ok ? 'loaded' : 'failed'));
    }, [store]);

    useEffect(() => {
        loadEditions();
        // Read into screen state above; the URL keeps only the edition.
        setParams({ tab: undefined, coupon: undefined });
    }, [loadEditions, setParams]);

    // The store outlives the page: the first pick of a visit always loads,
    // even the edition a previous visit left selected. State, not a ref: the
    // shell waits on it below.
    const [hasSelected, setHasSelected] = useState(false);
    useEffect(() => {
        if (editionsLoad !== 'loaded') {
            return;
        }
        const known = store.editions.some(({ key }) => key === requestedKey);
        const key = known ? requestedKey : store.editions[0]?.key;
        if (key && (!hasSelected || key !== store.editionKey)) {
            setHasSelected(true);
            store.selectEdition(key);
        }
    }, [editionsLoad, requestedKey, store, hasSelected]);

    // Through the guard: switching edition unmounts Setup and its edits.
    const switchEdition = useCallback(
        (key: string) =>
            checkNavigation(() => {
                setFocusCouponId(null);
                setParams({ edition: key }, { push: true });
            }),
        [checkNavigation, setParams]
    );

    const openCoupon = useCallback((promotionId: number) => {
        setTab('coupons');
        setFocusCouponId(promotionId);
    }, []);

    const clearCouponFocus = useCallback(() => setFocusCouponId(null), []);

    const tabBody = (content: React.ReactNode) => (
        <div className={style.body}>{content}</div>
    );

    // Until this visit picks its edition, the store still holds the last one.
    if (
        editionsLoad !== 'loaded' ||
        store.editions.length === 0 ||
        !hasSelected
    ) {
        return (
            <>
                <PageTopBar title={DOC31_PAGE_TITLE} />
                {editionsLoad === 'failed' ? (
                    tabBody(
                        <Empty description="Couldn't load the 31DOC editions.">
                            <Button onClick={loadEditions}>Try again</Button>
                        </Empty>
                    )
                ) : editionsLoad === 'loaded' && store.editions.length === 0 ? (
                    tabBody(
                        <Empty description="No 31DOC edition yet. Each year's edition arrives with its release." />
                    )
                ) : (
                    <div className={style.loading}>
                        <Spin />
                    </div>
                )}
            </>
        );
    }

    const notices = [
        store.isConfigurationStale && (
            <Alert
                key="stale"
                type="warning"
                showIcon
                message="Someone saved since you opened this. Reload to see their changes."
                action={
                    <Button size="small" onClick={() => store.reload()}>
                        Reload
                    </Button>
                }
            />
        ),
        store.isReadOnly && (
            <Alert
                key="closed"
                type="info"
                showIcon
                message="This edition is closed. Nothing can change."
            />
        ),
    ].filter(Boolean);

    return (
        <>
            <EditionHeader
                reasonPrompt={reasonPrompt}
                onSwitchEdition={switchEdition}
                onShowReadiness={() => setTab('setup')}
            />
            {store.view === null ? (
                <div className={style.loading}>
                    {store.isLoadingEdition || !store.editionKey ? (
                        <Spin />
                    ) : (
                        <Empty description="Couldn't load this edition.">
                            <Button
                                onClick={() =>
                                    store.editionKey &&
                                    store.selectEdition(store.editionKey)
                                }
                            >
                                Try again
                            </Button>
                        </Empty>
                    )}
                </div>
            ) : (
                <Doc31SetupFormProvider
                    view={store.view}
                    onOpenCoupon={openCoupon}
                    onShowTab={setTab}
                >
                    <TopTabs
                        variant="page-header"
                        activeKey={tab}
                        onChange={(key) => setTab(key as Doc31Tab)}
                        belowTabBar={
                            notices.length > 0 ? (
                                <div className={style.notices}>{notices}</div>
                            ) : undefined
                        }
                        tabsArray={[
                            {
                                key: 'setup',
                                name: 'Config',
                                children: tabBody(<Doc31SetupTab />),
                            },
                            {
                                key: 'catalog',
                                name: 'Bonuses & badges',
                                children: tabBody(<Doc31CatalogTab />),
                            },
                            {
                                key: 'coupons',
                                name: store.couponsLoadFailed
                                    ? 'Coupons'
                                    : `Coupons (${store.coupons.length})`,
                                children: tabBody(
                                    <Doc31CouponsTab
                                        focusCouponId={focusCouponId}
                                        onFocusHandled={clearCouponFocus}
                                    />
                                ),
                            },
                            {
                                key: 'history',
                                name: 'History',
                                children: tabBody(<Doc31HistoryTab />),
                            },
                        ]}
                    />
                </Doc31SetupFormProvider>
            )}
            {reasonPrompt.element}
        </>
    );
});
